import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isRelation } from "@/models/utils/semanticChecks";
import { SCREEN_SM } from "@/style/breakpoints";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { Alignment, InlineNodeAttrs } from "@corca/tiptap";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

export const INDENT_NODE_DATA_ATTRIBUTE = "data-indent-node-id";

/**
 * Math alignment is off on narrow screens: wrapped expressions there put their
 * operator somewhere else than on desktop, and indentation attrs are shared
 * through the doc, so narrow clients neither render nor write them.
 */
export function isMathIndentationDisabled(windowWidth = window.innerWidth): boolean {
  return windowWidth < SCREEN_SM;
}

export type IndentType = "relation" | "continuation";

function getReferenceNode(doc: DocumentModel, nodeId: string): OperatorSymbolNodeModel | undefined {
  const wrapperNode = doc.nodes.get(nodeId);

  if (!wrapperNode?.is(WrapperNodeModel) || getTargetNode(doc, nodeId)) {
    return undefined;
  }

  const { nodes = [] } = wrapperNode.inlineNode ?? {};

  return nodes.find(isRelation);
}

function getTargetNode(doc: DocumentModel, nodeId: string): OperatorSymbolNodeModel | undefined {
  const wrapperNode = doc.nodes.get(nodeId);

  if (!wrapperNode?.is(WrapperNodeModel)) {
    return undefined;
  }

  const [firstNode] = wrapperNode.inlineNode?.nodes ?? [];

  if (firstNode?.is(OperatorSymbolNodeModel)) {
    return firstNode;
  }

  return undefined;
}

function getIndentType(doc: DocumentModel, nodeId: string): IndentType | undefined {
  const wrapperNode = doc.nodes.get(nodeId);

  if (!wrapperNode?.is(WrapperNodeModel) || getReferenceNode(doc, nodeId)) {
    return undefined;
  }

  const targetNode = getTargetNode(doc, nodeId);

  if (targetNode) {
    return isRelation(targetNode) ? "relation" : "continuation";
  }

  return undefined;
}

type Expression = {
  nodeId: string;
  pos: number;
  width?: number | null;
  operatorLeftOffset?: number | null;
  operatorRightOffset?: number | null;
  indentType?: IndentType;
  /** How far the expression's line starts past the paragraph's other lines, in px. */
  lineOffset?: number;
};

export type IndentationGroup = {
  reference: Expression;
  targets: Expression[];
};

function hasContentAroundExpression(
  paragraph: PMNode,
  startIndex: number,
  side: "before" | "after" = "after",
): boolean {
  const nextIndex = side === "before" ? startIndex - 1 : startIndex + 1;
  if (nextIndex === -1 || nextIndex >= paragraph.childCount) {
    return false;
  }

  const nextChild = paragraph.child(nextIndex);
  if (nextChild.type.name === "hardBreak" || nextChild.type.name === "inlineExpression") {
    return false;
  }

  return true;
}

/**
 * @param firstLineIndent The paragraph's first-line indent in px, which shifts an expression that
 * starts the paragraph off the column of the lines below it.
 */
export function findIndentationGroups(
  paragraph: PMNode,
  paragraphPos: number,
  doc: DocumentModel,
  firstLineIndent = 0,
): IndentationGroup[] {
  const groups: IndentationGroup[] = [];

  let currentGroup: IndentationGroup | null = null;
  let hardBreakCount = 0;
  let currentPos = paragraphPos + 1;

  const finalizeGroup = () => {
    if (currentGroup && currentGroup.targets.length > 0) {
      groups.push(currentGroup);
    }
  };

  for (let i = 0; i < paragraph.childCount; i++) {
    const child = paragraph.child(i);

    if (child.type.name === "inlineExpression") {
      const {
        nodeId,
        width,
        indentNodeLeftOffset: operatorLeftOffset,
        indentNodeRightOffset: operatorRightOffset,
      } = child.attrs as InlineNodeAttrs;

      const isTarget = !!getTargetNode(doc, nodeId);
      const isRef = !!getReferenceNode(doc, nodeId);

      if (hardBreakCount > 1) {
        finalizeGroup();
        currentGroup = null;
      }

      if (isTarget && currentGroup && !hasContentAroundExpression(paragraph, i)) {
        const indentType = getIndentType(doc, nodeId);

        currentGroup.targets.push({
          nodeId,
          pos: currentPos,
          width,
          operatorLeftOffset,
          operatorRightOffset,
          indentType,
        });
      } else if (isRef && !hasContentAroundExpression(paragraph, i, "before")) {
        finalizeGroup();
        currentGroup = {
          reference: {
            nodeId,
            pos: currentPos,
            width,
            operatorLeftOffset,
            operatorRightOffset,
            lineOffset: i === 0 ? firstLineIndent : 0,
          },
          targets: [],
        };
      } else {
        finalizeGroup();
        currentGroup = null;
      }

      hardBreakCount = 0;
    } else if (child.type.name === "hardBreak") {
      hardBreakCount += 1;
    } else {
      finalizeGroup();
      currentGroup = null;
      hardBreakCount = 0;
    }

    currentPos += child.nodeSize;
  }

  finalizeGroup();

  return groups;
}

const OPERATOR_VISUAL_OFFSET = 2.5;

function getIndentOffset(expression: Expression, metrics: OperatorMetrics | null): number {
  if (!metrics) {
    return 0;
  }

  if (expression.indentType === "continuation" && metrics.operatorWidth) {
    return metrics.operatorWidth + OPERATOR_VISUAL_OFFSET;
  }

  return 0;
}

export function getGroupIndentations(
  { alignment, group }: { alignment: Alignment; group: IndentationGroup },
  resolveMetrics: OperatorMetricsResolver,
): Map<string, number> {
  const indentedNodeIds = new Map<string, number>();
  const referenceMetrics = resolveMetrics(group.reference);

  if (!referenceMetrics) {
    return indentedNodeIds;
  }

  switch (alignment) {
    case "left": {
      const indentation = Math.floor(
        referenceMetrics.leftOffset + (group.reference.lineOffset ?? 0) - OPERATOR_VISUAL_OFFSET,
      );

      for (const target of group.targets) {
        const targetMetrics = resolveMetrics(target);
        const indentOffset = getIndentOffset(target, targetMetrics);

        indentedNodeIds.set(target.nodeId, indentation + indentOffset);
      }

      return indentedNodeIds;
    }

    case "right": {
      const referenceIndentOffset = getIndentOffset(group.reference, referenceMetrics);
      const referenceRight = referenceMetrics.rightOffset + referenceIndentOffset;

      for (const target of group.targets) {
        const targetMetrics = resolveMetrics(target);

        if (targetMetrics) {
          const targetIndentOffset = getIndentOffset(target, targetMetrics);
          const targetRight = targetMetrics.rightOffset + targetIndentOffset;
          const indentation = Math.floor(targetRight - referenceRight);

          indentedNodeIds.set(target.nodeId, indentation);
        }
      }

      return indentedNodeIds;
    }

    // Centering a line splits its line offset between both sides.
    case "center": {
      const { operatorWidth, expressionWidth: referenceWidth } = referenceMetrics;

      if (operatorWidth && referenceWidth) {
        const referenceIndentOffset = getIndentOffset(group.reference, referenceMetrics);
        const referenceCenter =
          referenceMetrics.leftOffset +
          operatorWidth / 2 -
          referenceIndentOffset +
          (group.reference.lineOffset ?? 0) / 2;

        for (const target of group.targets) {
          const targetMetrics = resolveMetrics(target);

          if (targetMetrics && targetMetrics.operatorWidth && targetMetrics.expressionWidth) {
            const targetIndentOffset = getIndentOffset(target, targetMetrics);
            const targetCenter =
              targetMetrics.leftOffset +
              targetMetrics.operatorWidth / 2 -
              targetIndentOffset +
              (target.lineOffset ?? 0) / 2;
            const widthAdjustment = (referenceWidth - targetMetrics.expressionWidth) / 2;
            const indentation = Math.floor(referenceCenter - targetCenter - widthAdjustment);

            indentedNodeIds.set(target.nodeId, indentation);
          }
        }
      }

      return indentedNodeIds;
    }

    default: {
      return indentedNodeIds;
    }
  }
}

export function mapIndentationGroupToAlignment(
  { alignment, group }: { alignment: Alignment; group: IndentationGroup },
  resolveMetrics: OperatorMetricsResolver,
): IndentationGroup {
  switch (alignment) {
    case "left": {
      return group;
    }

    case "center": {
      const allExpressions = [group.reference, ...group.targets];
      const measurements = allExpressions
        .map((expr) => {
          const metrics = resolveMetrics(expr);

          if (!metrics) {
            return null;
          }

          const width = expr.width ?? metrics.expressionWidth;

          if (width === null) {
            return null;
          }

          return {
            expr,
            width,
          };
        })
        .filter((measurement) => measurement !== null);

      if (measurements.length === 0) {
        return group;
      }

      const reference = measurements.reduce((max, current) =>
        current.width > max.width ? current : max,
      );

      const newTargets = measurements
        .filter((m) => m.expr.nodeId !== reference.expr.nodeId)
        .map((m) => m.expr);

      return {
        reference: reference.expr,
        targets: newTargets,
      };
    }

    case "right": {
      const allExpressions = [group.reference, ...group.targets];
      const measurements = allExpressions
        .map((expr) => {
          const metrics = resolveMetrics(expr);

          if (!metrics) {
            return null;
          }

          const indentOffset = getIndentOffset(expr, metrics);

          return {
            expr,
            metrics,
            effectiveRightOffset: metrics.rightOffset + indentOffset,
            width: expr.width ?? 0,
          };
        })
        .filter((measurement) => measurement !== null);

      if (measurements.length === 0) {
        return group;
      }

      const reference = measurements.reduce((max, current) => {
        if (current.effectiveRightOffset > max.effectiveRightOffset) {
          return current;
        }

        if (
          current.effectiveRightOffset === max.effectiveRightOffset &&
          current.width > max.width
        ) {
          return current;
        }

        return max;
      });

      const newTargets = measurements
        .filter((m) => m.expr.nodeId !== reference.expr.nodeId)
        .map((m) => m.expr);

      return {
        reference: reference.expr,
        targets: newTargets,
      };
    }

    default: {
      return group;
    }
  }
}

type OperatorMetrics = {
  leftOffset: number;
  rightOffset: number;
  operatorWidth: number | null;
  expressionWidth: number | null;
};

export type OperatorMetricsResolver = (expression: Expression) => OperatorMetrics | null;

export function createOperatorMetricsResolver(
  doc: DocumentModel,
  view: EditorView,
): OperatorMetricsResolver {
  const cache = new Map<string, OperatorMetrics | null>();

  return (expression) => {
    const cacheKey = getExpressionCacheKey(expression);

    if (cache.has(cacheKey)) {
      const metrics = cache.get(cacheKey);

      if (metrics) {
        return metrics;
      }

      cache.delete(cacheKey);
      return null;
    }

    const metrics = computeOperatorMetrics(expression, doc, view);

    cache.set(cacheKey, metrics);
    return metrics;
  };
}

function getExpressionCacheKey(expression: Expression) {
  const { nodeId, width, operatorLeftOffset, operatorRightOffset, indentType } = expression;
  return [
    nodeId,
    width ?? "_",
    operatorLeftOffset ?? "_",
    operatorRightOffset ?? "_",
    indentType ?? "_",
  ].join("::");
}

function computeOperatorMetrics(
  expression: Expression,
  doc: DocumentModel,
  view: EditorView,
): OperatorMetrics | null {
  const cachedLeft = expression.operatorLeftOffset;
  const cachedRight = expression.operatorRightOffset;
  const cachedWidth = typeof expression.width === "number" ? expression.width : null;

  if (typeof cachedLeft === "number" && typeof cachedRight === "number") {
    const operatorWidth =
      cachedWidth !== null ? Math.max(cachedWidth - cachedLeft - cachedRight, 0) : null;

    return {
      leftOffset: cachedLeft,
      rightOffset: cachedRight,
      operatorWidth,
      expressionWidth: cachedWidth,
    };
  }

  return getOperatorMetricsFromDOM(expression, doc, view);
}

function getOperatorMetricsFromDOM(
  expression: Expression,
  doc: DocumentModel,
  view: EditorView,
): OperatorMetrics | null {
  const operator = findIndentOperator(doc, expression.nodeId);

  if (!operator) {
    return null;
  }

  try {
    if (expression.pos < 0 || expression.pos >= view.state.doc.content.size) {
      return null;
    }

    const exprDom = view.nodeDOM(expression.pos);

    if (!exprDom) {
      return null;
    }

    const operatorEl = (exprDom as HTMLElement).querySelector(
      `[${INDENT_NODE_DATA_ATTRIBUTE}="${operator.id}"]`,
    );

    if (!operatorEl) {
      return null;
    }

    const exprRect = (exprDom as HTMLElement).getBoundingClientRect();
    const operatorRect = operatorEl.getBoundingClientRect();

    return {
      leftOffset: operatorRect.left - exprRect.left,
      rightOffset: exprRect.right - operatorRect.right,
      operatorWidth: operatorRect.width,
      expressionWidth: exprRect.width,
    };
  } catch (_error) {
    return null;
  }
}

export function findIndentOperator(
  doc: DocumentModel,
  nodeId: string,
): OperatorSymbolNodeModel | undefined {
  const wrapperNode = doc.nodes.get(nodeId);

  if (!wrapperNode?.is(WrapperNodeModel) || !wrapperNode.inlineNode) {
    return undefined;
  }

  return getReferenceNode(doc, nodeId) || getTargetNode(doc, nodeId);
}

export function getIndentationAttrs(node: NodeModel) {
  switch (node.type) {
    case "operatorSymbol": {
      return {
        [INDENT_NODE_DATA_ATTRIBUTE]: node.id,
      };
    }

    default: {
      return {};
    }
  }
}
